import React from 'react';
import { resolveStaticUrl } from '../api/axiosClient';

// ─── Shared style helpers ─────────────────────────────────────────────────────

const card = {
  background: 'white',
  border: '2px solid #d4af37',
  borderRadius: '8px',
  padding: '30px',
  marginBottom: '30px',
};

const cardTitle = {
  marginTop: 0,
  color: '#1a1a1a',
  fontSize: '20px',
  fontWeight: '700',
  marginBottom: '25px',
};

const fieldLabel = {
  display: 'block',
  fontSize: '12px',
  color: '#666',
  marginBottom: '6px',
  fontWeight: '600',
};

const fieldValue = {
  fontSize: '16px',
  color: '#1a1a1a',
  fontWeight: '500',
  padding: '12px',
  background: '#f8f8f8',
  borderRadius: '4px',
};

// ─── InfoField ────────────────────────────────────────────────────────────────

function InfoField({ label, value, fullWidth, isLink, capitalize }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <label style={fieldLabel}>{label}</label>
      <div style={{ ...fieldValue, ...(capitalize ? { textTransform: 'capitalize' } : {}) }}>
        {isLink && value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#d4af37', textDecoration: 'none' }}
          >
            {value}
          </a>
        ) : (
          value || 'Not specified'
        )}
      </div>
    </div>
  );
}

// ─── CompanySettings ─────────────────────────────────────────────────────────

/**
 * Unified company settings display + logo upload.
 * Works for any company type: ICSC (is_system), Certification Body, or Client.
 *
 * Props:
 *   company       – company object from GET /clients/{id}
 *   logoPreview   – data-URL for newly-selected file (null when none)
 *   logoFile      – File object for currently selected file (null when none)
 *   onLogoChange  – handler for <input type="file"> onChange
 *   onLogoUpload  – handler for upload button onClick
 */
function CompanySettings({ company, logoPreview, logoFile, onLogoChange, onLogoUpload }) {
  if (!company) return null;

  const companyLabel = company.is_system
    ? 'ICSC — System Company'
    : company.is_third_party_admin
    ? 'Certification Body Information'
    : 'Company Information';

  return (
    <div>
      {/* ── Logo ─────────────────────────────────────────────────────────── */}
      <div style={card}>
        <h2 style={cardTitle}>Company Logo</h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '30px',
            alignItems: 'start',
          }}
        >
          {/* Preview pane */}
          <div
            style={{
              background: '#f8f8f8',
              border: '2px dashed #d4af37',
              borderRadius: '6px',
              padding: '30px',
              textAlign: 'center',
              minHeight: '250px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" style={{ maxWidth: '100%', maxHeight: '200px' }} />
            ) : company.logo_url ? (
              <img src={resolveStaticUrl(company.logo_url)} alt="Company logo" style={{ maxWidth: '100%', maxHeight: '200px' }} />
            ) : (
              <div style={{ color: '#999', fontSize: '14px' }}>No logo uploaded yet</div>
            )}
          </div>

          {/* Upload controls */}
          <div>
            <label style={{ display: 'block', marginBottom: '15px', fontWeight: '600', color: '#1a1a1a', fontSize: '14px' }}>
              Upload Company Logo
            </label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              onChange={onLogoChange}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px',
                border: '1px solid #d4af37',
                borderRadius: '6px',
                marginBottom: '15px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            />
            <button
              onClick={onLogoUpload}
              disabled={!logoFile}
              style={{
                width: '100%',
                padding: '12px',
                background: logoFile ? '#d4af37' : '#ddd',
                color: logoFile ? '#1a1a1a' : '#999',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: logoFile ? 'pointer' : 'not-allowed',
                marginBottom: '10px',
              }}
            >
              Upload Logo
            </button>
            <p style={{ fontSize: '12px', color: '#999', margin: '10px 0 0 0' }}>
              Max 2 MB &middot; Formats: PNG, JPG, JPEG, SVG
            </p>
          </div>
        </div>
      </div>

      {/* ── Company Info ─────────────────────────────────────────────────── */}
      <div style={card}>
        <h2 style={cardTitle}>{companyLabel}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
          <InfoField label="Company Name" value={company.name} />
          <InfoField label="Industry" value={company.industry} />
          <InfoField label="Company Size" value={company.size} capitalize />
          <InfoField label="Country" value={company.country} />
          <InfoField label="Address" value={company.address} fullWidth />
          <InfoField label="Website" value={company.website} fullWidth isLink />
          <InfoField
            label="Implementation Status"
            value={company.implementation_status}
            fullWidth
            capitalize
          />
        </div>
      </div>
    </div>
  );
}

export default CompanySettings;
